// In-DOM confirmation modal. Resolves true on confirm, false on cancel,
// Escape, or backdrop click. We deliberately avoid window.confirm(): native
// dialogs block the browser-automation extension (and the event loop), and
// can't be asserted against in jsdom. This is the reusable security-action
// confirm used by the Devices & sessions screen.

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  // 'danger' tints the confirm button with the error color for destructive
  // actions (revoke / sign out); 'default' uses the accent.
  tone?: 'default' | 'danger';
}

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  const previouslyFocused = document.activeElement as HTMLElement | null;

  const overlay = document.createElement('div');
  overlay.className = 'confirm';
  overlay.dataset.tone = opts.tone ?? 'default';

  const dialog = document.createElement('div');
  dialog.className = 'confirm__dialog';
  dialog.setAttribute('role', 'alertdialog');
  dialog.setAttribute('aria-modal', 'true');

  const titleId = 'confirm-title';
  const descId = 'confirm-desc';
  dialog.setAttribute('aria-labelledby', titleId);
  dialog.setAttribute('aria-describedby', descId);

  const title = document.createElement('h2');
  title.className = 'confirm__title';
  title.id = titleId;
  title.textContent = opts.title;

  const message = document.createElement('p');
  message.className = 'confirm__message';
  message.id = descId;
  message.textContent = opts.message;

  const actions = document.createElement('div');
  actions.className = 'confirm__actions';

  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'confirm__btn confirm__btn--cancel';
  cancel.textContent = opts.cancelLabel ?? 'Cancel';

  const confirm = document.createElement('button');
  confirm.type = 'button';
  confirm.className = 'confirm__btn confirm__btn--confirm';
  confirm.textContent = opts.confirmLabel ?? 'Confirm';

  actions.append(cancel, confirm);
  dialog.append(title, message, actions);
  overlay.append(dialog);

  return new Promise<boolean>((resolve) => {
    let settled = false;

    function close(result: boolean): void {
      if (settled) return;
      settled = true;
      document.removeEventListener('keydown', onKeydown, true);
      overlay.remove();
      // Restore focus to whatever launched the dialog so keyboard users
      // land back on the triggering control.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
      resolve(result);
    }

    function onKeydown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(false);
        return;
      }
      if (e.key === 'Tab') {
        const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const activeEl = document.activeElement as HTMLElement | null;
        if (e.shiftKey && activeEl === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && activeEl === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    cancel.addEventListener('click', () => close(false));
    confirm.addEventListener('click', () => close(true));
    overlay.addEventListener('click', (e) => {
      // Backdrop click (outside the dialog) cancels; clicks inside don't.
      if (e.target === overlay) close(false);
    });
    document.addEventListener('keydown', onKeydown, true);

    document.body.append(overlay);
    // Default focus to Cancel — the safe choice for destructive confirms.
    cancel.focus();
  });
}
